import { useQuery } from 'convex/react';
import { useMemo } from 'react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig — all hooks in src/hooks/ use this relative path
import { getInitials, getAvatarColor, formatTime, extractFromName } from '@/lib/message-utils';
import type { IUnifiedInboxItem, IUseUnifiedInboxReturn } from '@/@types/messages';

export function useUnifiedInbox(): IUseUnifiedInboxReturn {
  // Subscribe to Telegram conversations
  const rawConversations = useQuery(api.telegram.personal.queries.getConversations, {});

  // Subscribe to Gmail INBOX emails (limit 50)
  const rawEmails = useQuery(api.gmail.queries.getEmails, { label: 'INBOX', limit: 50 });

  // Subscribe to WhatsApp conversations
  const rawWhatsAppConversations = useQuery(api.whatsapp.queries.getConversations, {});

  const items = useMemo((): IUnifiedInboxItem[] => {
    const result: IUnifiedInboxItem[] = [];

    // Map Telegram conversations
    if (rawConversations !== undefined) {
      for (const conv of rawConversations) {
        result.push({
          id: `telegram-${conv.telegramContactId}`,
          source: 'telegram',
          name: conv.contactName,
          initials: getInitials(conv.contactName),
          avatarColor: getAvatarColor(conv.telegramContactId),
          preview: conv.lastMessage,
          sortTimestamp: conv.lastMessageTimestamp,
          formattedTime: formatTime(conv.lastMessageTimestamp),
          isUnread: conv.unreadCount > 0,
          telegramContactId: conv.telegramContactId,
        });
      }
    }

    // Map Gmail emails, deduplicate by threadId (keep newest per thread)
    if (rawEmails !== undefined) {
      const seenThreads = new Set<string>();
      for (const email of rawEmails) {
        if (seenThreads.has(email.threadId)) continue;
        seenThreads.add(email.threadId);
        result.push({
          id: `gmail-${email.threadId}`,
          source: 'gmail',
          name: extractFromName(email.from),
          initials: getInitials(extractFromName(email.from)),
          avatarColor: getAvatarColor(email.threadId),
          preview: email.subject || email.snippet || '(no subject)',
          sortTimestamp: email.internalDate,
          formattedTime: formatTime(email.internalDate),
          isUnread: !email.isRead,
          gmailThreadId: email.threadId,
        });
      }
    }

    // Map WhatsApp conversations
    if (rawWhatsAppConversations !== undefined) {
      for (const conv of rawWhatsAppConversations) {
        result.push({
          id: `whatsapp-${conv.whatsappContactId}`,
          source: 'whatsapp',
          name: conv.contactName,
          initials: getInitials(conv.contactName),
          avatarColor: getAvatarColor(conv.whatsappContactId),
          preview: conv.lastMessage || '(no content)',
          sortTimestamp: conv.lastMessageTimestamp,
          formattedTime: formatTime(conv.lastMessageTimestamp),
          isUnread: conv.unreadCount > 0,
          whatsappContactId: conv.whatsappContactId,
        });
      }
    }

    // Sort newest first
    result.sort((a, b) => b.sortTimestamp - a.sortTimestamp);
    return result;
  }, [rawConversations, rawEmails, rawWhatsAppConversations]);

  return {
    items,
    isLoading:
      rawConversations === undefined ||
      rawEmails === undefined ||
      rawWhatsAppConversations === undefined,
  };
}
